import { routerNavigatedAction } from '@ngrx/router-store';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { jobsFeature } from 'src/app/jobs/jobs.reducer';

import { JobDetailsV2Fragment } from '../../../../generated/graphql.generated';
import { JobToolActions as JobToolV3Actions } from '../../../job/job-tool.actions';
import { LastOpenedJobPage, loadFromStorage } from '../../../state/persist-state.utils';
import { UnsavedChange, unsavedChangesFeature } from '../../../state/unsaved-changes.reducer';
import { CallState, LoadingState } from '../../../utilities/state.util';

import { QuickAccessActions } from './quick-access.actions';


const RECENT_JOBS_MAX = 3;
const PINNED_JOBS_MAX = 3;

export const quickAccessInitialState = {
  pinnedJobs: loadFromStorage('pinnedJobs', []),
  recentJobs: loadFromStorage('recentJobs', []),
  lastOpenedJobPageByJobId: loadFromStorage('lastOpenedJobPageByJobId', {}),
  loading: LoadingState.INIT as CallState,
  expanded: true as boolean,

} as const;

export type QuickAccessState = typeof quickAccessInitialState;

export const quickAccessFeature = createFeature({
  name: 'Quick Access',
  reducer: createReducer(
    quickAccessInitialState,
    on(QuickAccessActions.toogleQuickAccess, (state): QuickAccessState => {

      return {
        ...state,
        expanded: !state.expanded,
      };
    }),
    on(
      JobToolV3Actions.jobLoaded,
      (state, { job: { id: jobId } }): QuickAccessState => {

        if (state.recentJobs.includes(jobId)) { return state; }

        const recentJobs = [
          jobId,
          ...state.recentJobs.filter((id) => id !== jobId),
        ].slice(0, RECENT_JOBS_MAX);

          return {
            ...state,
            recentJobs,
          }
      }),
    on(
      JobToolV3Actions.fetchJobError,
      (state, action): QuickAccessState => {

        if (!action.removeFromQuickAccess) { return state; }

        const { [action.jobId]: _removedPage, ...lastOpenedJobPageByJobId } = state.lastOpenedJobPageByJobId;

        return {
          ...state,
          recentJobs: state.recentJobs.filter((jobId) => jobId !== action.jobId ),
          lastOpenedJobPageByJobId,
        }
      }),
    on(
      JobToolV3Actions.deleteJobSuccess,
      (state, action): QuickAccessState => {

        const { [action.jobId]: _removedPage, ...lastOpenedJobPageByJobId } = state.lastOpenedJobPageByJobId;

        return {
          ...state,
          recentJobs: state.recentJobs.filter((jobId) => jobId !== action.jobId ),
          lastOpenedJobPageByJobId,
        }
      }),
      on(QuickAccessActions.jobPinned, (state, { jobId }): QuickAccessState => {

        const pinnedJobs = [
          jobId,
          ...state.pinnedJobs.filter((id) => id !== jobId),
        ].slice(0, PINNED_JOBS_MAX);

          return {
            ...state,
            pinnedJobs,
          };
      }),
      on(routerNavigatedAction, (state, { payload: { event: { urlAfterRedirects } } }): QuickAccessState => {

        const trackedPage = getTrackedJobPage(urlAfterRedirects);

        if (!trackedPage) { return state; }

        const { jobId, page } = trackedPage;

        if (state.lastOpenedJobPageByJobId[jobId] === page) { return state; }

        return {
          ...state,
          lastOpenedJobPageByJobId: {
            ...state.lastOpenedJobPageByJobId,
            [jobId]: page,
          },
        };
      }),
  )
})

export const selectRecentJobs = createSelector(
  jobsFeature.selectJobs,
  quickAccessFeature.selectRecentJobs,
  unsavedChangesFeature.selectQueue,
  quickAccessFeature.selectLastOpenedJobPageByJobId,
  (jobs, recentJobs, unsavedChanges, lastOpenedJobPageByJobId) => recentJobs
  .map((jobId) => jobs[jobId])
  .filter(Boolean)
  .map((job) => formatJob(
    job,
    unsavedChanges,
    resolveJobLink(job.id, lastOpenedJobPageByJobId),
  )),
);

export const selectPinnedJobs = createSelector(
  jobsFeature.selectJobs,
  quickAccessFeature.selectPinnedJobs,
  unsavedChangesFeature.selectQueue,
  quickAccessFeature.selectLastOpenedJobPageByJobId,
  (jobs, pinnedJobs, unsavedChanges, lastOpenedJobPageByJobId) => pinnedJobs
  .map((jobId) => jobs[jobId])
  .filter(Boolean)
  .map((job) => formatJob(
    job,
    unsavedChanges,
    resolveJobLink(job.id, lastOpenedJobPageByJobId),
  )),
);

const formatJob = (job: JobDetailsV2Fragment, unsavedChanges: UnsavedChange[], jobLink: string[]) => ({
  ...job,
  hasChanges: unsavedChanges.some((c) => c.jobId === job.id),
  contextZone: job.zone.type === 'area' ? job.zone.parent?.id : job.zone.id,
  jobLink,
});

function getTrackedJobPage(urlAfterRedirects: string): { jobId: string; page: LastOpenedJobPage } | undefined {
  const pathname = new URL(urlAfterRedirects, window.location.origin).pathname;
  const [ first, second, third ] = pathname.split('/').filter(Boolean);

  // View lives at /jobs/:jobId/...; edit at /jobs/:jobId/edit; create at /jobs/new.
  if (first !== 'jobs' || !second || second === 'new') {
    return undefined;
  }

  return { jobId: second, page: third === 'edit' ? 'edit' : 'view' };
}

function resolveJobLink(
  jobId: string,
  lastOpenedJobPageByJobId: Record<string, LastOpenedJobPage>,
): string[] {
  const lastOpenedPage = lastOpenedJobPageByJobId[jobId] || 'view';

  if (lastOpenedPage === 'edit') {
    return ['/jobs', jobId, 'edit'];
  }

  return ['/jobs', jobId, 'overview'];
}
